import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { useLazyGetCsvQuery, useLazyGetGoogleSheetsQuery } from 'services';
import { Table, TableSelection, useTableSelection } from 'features/table';
import { Button, Icon, SelectProfileTooltip, Text } from 'shared/components/ui';
import { useFilters, useGetCandidatesInfinite } from 'features/search/hooks';
import { ProgAIProfile, ProgAIResults } from 'shared/generated-models';
import {
	ProfileRow,
	ResultsEmpty,
	ResultsError,
	ResultsNotSelected,
	ResultsSkeleton,
	SearchUpdate
} from 'features/search/components';
import { ProjectMenu } from 'features/projects';
import { CandidateModal } from 'features/candidate';
import { Analytics } from 'features/analytics';
import { useIsFree } from 'features/auth';
import { ExportToPushMenu } from 'features/integrations';
import { getFiltersText } from 'features/search';

import styles from './index.module.scss';

interface Props {
	outerScrollContainerRef: RefObject<HTMLElement>;
}

const getFiltersKey = (filters: ReturnType<typeof useFilters>) => {
	const { page: _page, size: _size, ...baseFilters } = filters;

	return JSON.stringify(baseFilters);
};

export const ResultsListWithInfiniteScroll = ({ outerScrollContainerRef }: Props) => {
	const [candidate, setCandidate] = useState<ProgAIResults | null>(null);
	const [pendingNextCandidateIndex, setPendingNextCandidateIndex] = useState<number | null>(null);

	const isFree = useIsFree();
	const filters = useFilters();
	const sentinelRef = useRef<HTMLDivElement>(null);

	const filtersKey = useMemo(() => getFiltersKey(filters), [filters]);

	const [getCsv] = useLazyGetCsvQuery();
	const [getGoogleSheetsUrl] = useLazyGetGoogleSheetsQuery();

	const {
		results,
		resultsCount,
		searchResult,
		totalLoaded,
		isFetchingInitial,
		isFetchingMore,
		isError,
		hasMore,
		loadMore
	} = useGetCandidatesInfinite();

	const closeCandidateModal = () => {
		setCandidate(null);
		setPendingNextCandidateIndex(null);
	};

	useEffect(() => {
		outerScrollContainerRef.current?.scroll({ top: 0, behavior: 'smooth' });
		setCandidate(null);
		setPendingNextCandidateIndex(null);
	}, [filtersKey, outerScrollContainerRef]);

	useEffect(() => {
		if (searchResult) {
			Analytics.trackSearch(searchResult);
		}
	}, [searchResult]);

	useEffect(() => {
		const outerContainer = outerScrollContainerRef.current;
		const sentinel = sentinelRef.current;

		if (!outerContainer || !sentinel || !hasMore) return;

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0]?.isIntersecting) {
					void loadMore();
				}
			},
			{ root: outerContainer, rootMargin: '200px 0px' }
		);

		observer.observe(sentinel);

		return () => {
			observer.disconnect();
		};
	}, [hasMore, loadMore, outerScrollContainerRef, results.length]);

	useEffect(() => {
		if (pendingNextCandidateIndex === null) return;

		const nextCandidate = results[pendingNextCandidateIndex];
		if (!nextCandidate) return;

		setCandidate(nextCandidate);
		setPendingNextCandidateIndex(null);
	}, [pendingNextCandidateIndex, results]);

	const {
		selectAll,
		isAllSelected,
		toggle,
		isSelected,
		someSelected,
		count,
		selectCount,
		included,
		excluded,
		togglePage,
		selectedCount,
		mode
	} = useTableSelection<ProgAIProfile>({
		data: results.map(row => row.profile),
		totalCount: resultsCount,
		max: resultsCount
	});

	const handleSelectLoadedResults = () => {
		if (results.length === 0) return;

		selectCount(results.length);
	};

	const handleExportCsv = (isFull: boolean) => {
		Analytics.trackCsvExport(count, 'Search');
		getCsv({
			ids: included,
			filters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull,
			filters_text: getFiltersText(filters)
		});
	};

	const handleExportGoogleSheets = (isFull: boolean) => {
		Analytics.trackGoogleSheetsExport(count, 'Search');
		getGoogleSheetsUrl({
			ids: included,
			filters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull
		});
	};

	useEffect(() => {
		const saved = localStorage.getItem('peopleParams');
		if (saved) {
			localStorage.removeItem('peopleParams');
			try {
				getGoogleSheetsUrl(JSON.parse(saved));
			} catch (parseErr) {
				console.error('Failed to parse saved export params:', parseErr);
			}
		}
	}, [getGoogleSheetsUrl]);

	const textInFilters = () => {
		if ('text' in filters) {
			if (resultsCount && resultsCount > 3000) return true;
		}
		return false;
	};

	const formatNumberWithCommas = (number: number) => {
		return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	};

	const getResultsCount = () => {
		if (!resultsCount) return `More than ${formatNumberWithCommas(totalLoaded)} candidates`;
		if (textInFilters()) return 'more than 3,000';
		return `${formatNumberWithCommas(resultsCount)} candidates`;
	};

	const currentCandidateIndex = candidate
		? results.findIndex(result => result.profile.id === candidate.profile.id)
		: -1;

	const canGoPrevious = currentCandidateIndex > 0;
	const canGoNext = currentCandidateIndex >= 0 && (currentCandidateIndex < results.length - 1 || hasMore);

	const handlePreviousCandidate = useCallback(() => {
		if (currentCandidateIndex <= 0) return;

		setCandidate(results[currentCandidateIndex - 1]);
	}, [currentCandidateIndex, results]);

	const handleNextCandidate = useCallback(async () => {
		if (currentCandidateIndex < 0) return;

		if (currentCandidateIndex < results.length - 1) {
			setCandidate(results[currentCandidateIndex + 1]);
			return;
		}

		if (!hasMore || isFetchingMore) return;

		setPendingNextCandidateIndex(currentCandidateIndex + 1);

		const didLoad = await loadMore();
		if (!didLoad) {
			setPendingNextCandidateIndex(null);
			return;
		}
	}, [currentCandidateIndex, hasMore, isFetchingMore, loadMore]);

	if (isFetchingInitial) return <ResultsSkeleton />;

	if (isError && results.length === 0) {
		const filterKeys = Object.keys(filters);
		if (filterKeys.length === 2) {
			return <ResultsNotSelected />;
		}
		return <ResultsError />;
	}

	if (results.length === 0) return <ResultsEmpty />;

	return (
		<section className={styles.container}>
			<div className={styles.info}>
				<Text variant="grotesk/18/bold">{getResultsCount()}</Text>
			</div>
			<Table className={styles.list}>
				<div className={cn(styles.header, { [styles.headerBorder]: !isFree })}>
					<div className={styles.listControl}>
						<TableSelection
							controls={{
								main: { togglePage, count, isAllSelected },
								extra: { selectPage: handleSelectLoadedResults, selectAll, selectCount }
							}}
							hint="Select candidates"
						/>
						<div className={styles.infoButtons}>
							<SelectProfileTooltip isDisabled={!someSelected}>
								<span>
									<ProjectMenu
										candidates={{
											count,
											ids: included,
											excluded_ids: excluded,
											top: selectedCount || undefined
										}}
										disabled={!someSelected}>
										<Button
											type="default"
											className={styles.project}
											suffix={<Icon icon="arrow-down" />}>
											Add to project
										</Button>
									</ProjectMenu>
								</span>
							</SelectProfileTooltip>
							<SelectProfileTooltip isDisabled={!someSelected}>
								<span>
									<ExportToPushMenu
										disabled={!someSelected}
										handleExportCsv={handleExportCsv}
										handleExportGoogleSheets={handleExportGoogleSheets}
										candidates={{
											count,
											ids: included,
											excluded_ids: excluded,
											top: selectedCount || undefined,
											filters
										}}>
										<Button
											type="primary"
											disabled={!someSelected}
											suffix={<Icon icon="arrow-down" />}>
											Export to
										</Button>
									</ExportToPushMenu>
								</span>
							</SelectProfileTooltip>
						</div>
					</div>
					<SearchUpdate />
				</div>
				{results.map((result, idx) => (
					<ProfileRow
						checkbox={{
							checked: isSelected({ row: result.profile, idx }),
							onChange: () => toggle({ row: result.profile, idx }),
							mode
						}}
						key={result.profile.id}
						onClick={() => setCandidate(result)}
						candidate={result}
					/>
				))}
				<div className={styles.footer}>
					{isFetchingMore && (
						<div className={styles.loader}>
							<Text variant="inter/13/regular" color="darker_grey">
								Loading more candidates...
							</Text>
						</div>
					)}
					{!isFetchingMore && !hasMore && (
						<Text variant="inter/13/regular" color="darker_grey">
							End of results
						</Text>
					)}
				</div>
				<div className={styles.sentinel} ref={sentinelRef} />
			</Table>
			{candidate && (
				<CandidateModal
					candidates={results}
					setCandidate={setCandidate}
					candidate={candidate}
					onClose={closeCandidateModal}
					total={resultsCount}
					page={0}
					onChange={() => undefined}
					isPreviousDisabled={!canGoPrevious}
					isNextDisabled={!canGoNext || isFetchingMore}
					onPrevious={handlePreviousCandidate}
					onNext={() => {
						void handleNextCandidate();
					}}
				/>
			)}
		</section>
	);
};
